"use client";

/**
 * 运行状态区（2026-10-08）。
 *
 * 数据来自既有 `/api/hermes/runtime`（见 `use-runtime-status.ts` 的复用说明）。
 *
 * ## 🔴 三态必须视觉可分
 * `ok`（绿）/ `warn`（琥珀）/ `error`（红）/ `unknown`（灰）。
 * 最容易做错的是把 `unknown` 当成异常：ComfyUI 未探测时标红，
 * 用户会跑去查一台根本没问题的机器 —— 这是**制造焦虑**而不是提供信息。
 *
 * ## 异常项必须有「快速定位」
 * 只说「ComfyUI 不可达」而不给链接，用户还得自己回忆去哪个页面看。
 * 每条 warn/error 都带一个跳转（设置页 / 流水线页）。
 */

import { useTranslations } from "next-intl";
import Link from "next/link";
import { useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { F } from "@/components/studio/ui";
import { apiFetch } from "@/lib/api-fetch";
import { relativeTimeOf } from "@/lib/project-stats";
import { useRuntimeStatus } from "@/lib/home/use-runtime-status";

import {
  ChartSkeleton,
  HomeChip,
  HomeEmpty,
  HomeError,
  HomeSection,
  StatusDot,
  healthStyle,
} from "./home-primitives";

/** 把 ISO 时间渲染成「3 天前 / 5 分钟前 / 具体日期」。 */
function useTimeLabel() {
  const t = useTranslations("common");
  const tt = useTranslations("timeAgo");
  return (iso: string): string => {
    const rel = relativeTimeOf(new Date(iso));
    switch (rel.kind) {
      case "justNow":
        return tt("justNow");
      case "minutes":
        return tt("minutes", { count: rel.value });
      case "hours":
        return tt("hours", { count: rel.value });
      case "days":
        return tt("days", { count: rel.value });
      default:
        // 超过 30 天给绝对日期：逐日变化的天数只是噪声（与 project-stats 的分档一致）
        return rel.value.toLocaleDateString();
    }
  };
}

export function RuntimeStatusPanel({
  locale,
  className = "",
}: {
  locale: string;
  /** 🔴 明细行的高度差吸收器标记（第 9 轮）：dashboard 传 flex-1，
   *  两卡 stretch 对齐底边；列表顶对齐 + 行距加宽消化差值（第 12 轮）。 */
  className?: string;
}) {
  const t = useTranslations("home");
  const tc = useTranslations("common");
  // 🔴 根翻译器（无参）：service.labelKey / detailKey 是 lib/home/use-runtime-status.ts
  // 里写死的 `home.status.*` 根路径，用命名空间翻译器会解析成 `home.home.status.*`。
  const tRoot = useTranslations();
  const timeLabel = useTimeLabel();
  const { status, data, error, refresh } = useRuntimeStatus();

  const issueCount = data?.services.filter((s) => s.health === "error" || s.health === "warn").length ?? 0;

  // ── 关闭看门狗（2026-10-09 用户：「增加一个关闭看门狗的操作项目，
  //    避免历史废任务持续触发」）────────────────────────────────
  // 与队列面板「全部停止」里的那次 POST 同一个端点，但这里是**只关看门狗**：
  // 不动在跑的批任务（我们自己的参考视频批正在跑，绝不能连它一起停）。
  // 停机闩落下后，三处自愈入口（instrumentation / 健康轮询 / generate）都被拦住，
  // 入队新任务也**不会**解除（queue.ts 2026-10-04 起不再 resume）——
  // 关了就是关了，直到进程重启或显式 resume。
  const [stoppingWatchdog, setStoppingWatchdog] = useState(false);
  const closeWatchdog = async () => {
    setStoppingWatchdog(true);
    try {
      const res = await apiFetch("/api/runtime/watchdog", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "stop" }),
      });
      if (res.ok) {
        toast.success(t("status.watchdogStopped"));
        await refresh();
      } else {
        toast.error(t("status.watchdogStopFailed"));
      }
    } catch {
      toast.error(t("status.watchdogStopFailed"));
    } finally {
      setStoppingWatchdog(false);
    }
  };

  return (
    <HomeSection
      title={t("status.title")}
      hint={t("status.hint")}
      action={
        <div className="flex items-center gap-1.5">
          {/* 汇总徽标：0 问题时是绿色「全部正常」，有问题时给可点击的告警数 */}
          {status === "ok" && data ? (
            issueCount === 0 ? (
              <HomeChip>{t("status.allOk")}</HomeChip>
            ) : (
              <HomeChip active>{t("status.issueCount", { count: issueCount })}</HomeChip>
            )
          ) : null}
          <HomeChip onClick={() => void refresh()} aria-label={tc("retry")}>
            <RefreshCw className={`h-3 w-3 ${status === "loading" ? "animate-spin" : ""}`} />
          </HomeChip>
        </div>
      }
      bodyClassName="flex flex-col gap-1.5"
      className={className}
    >
      {/* 🔴 三态各有各的渲染：加载中/失败/成功。失败给重试，不静默。 */}
      {status === "loading" ? (
        <ChartSkeleton rows={4} />
      ) : status === "error" ? (
        <HomeError message={error ?? t("status.loadFailed")} onRetry={() => void refresh()} />
      ) : data && data.services.length > 0 ? (
        <>
          {/* 🔴 服务列表**顶对齐**（第 12 轮修订，推翻第 9 轮的 justify-center）：
              本卡只有 4 行，排行卡有 7 行 —— 居中会让 4 行悬浮在卡中央，
              与左卡的行完全不对齐，上下各留一大块空白（用户截图「排版错位」）。
              顶对齐让两卡首行对齐同一节奏；行距 py-3 加宽消化大部分高度差，
              残余空隙沉到列表与页脚之间 —— 卡内留白 + 页脚锚底，读作呼吸感
              而不是破洞（第 8 轮的红洞是页面底色露在卡**外**，形态不同）。 */}
          <div className="flex min-w-0 flex-1 flex-col justify-start">
            <ul className="flex flex-col divide-y divide-[var(--border-subtle)]">
            {data.services.map((s) => {
              const style = healthStyle(s.health);
              const detail = s.detailKey ? tRoot(s.detailKey) : s.detail;
              // 🔴 信息分层：正常项**不渲染技术详情**（pid / 显存 / 队列数）。
              // 首版四项全用等宽彩色字常驻，视觉后果有二：
              //   ① 一屏 4 行全是 `pid 28132 · 280 min 空闲` 这类等宽英文数字，
              //      与左边业务数据（中文标签 + 图表）处在同一视觉平面 → 用户反馈
              //      「排版不统一」，本质是**两种信息类型共用一套排版**；
              //   ② 全绿时一屏四行荧光色字，绿色铺满反而**降低**了告警的显著性 ——
              //      真出问题时那一条红混在四条绿里，用户扫不出来。
              // 契约：正常=服务名 + 相对时间（可扫读）；异常/警告=才展开技术详情
              // （此时用户正要排查，pid/显存正是他要的）。
              const showDetail = s.health === "error" || s.health === "warn";

              return (
                <li
                  key={s.id}
                  className="flex items-center gap-2 py-3"
                  data-service={s.id}
                  data-show-detail={showDetail ? "1" : "0"}
                >
                  <StatusDot health={s.health} pulse={s.health === "error"} />
                  <span className={`${F.meta} min-w-0 shrink-0 truncate`}>{tRoot(s.labelKey)}</span>

                  {showDetail ? (
                    <span
                      className={`min-w-0 flex-1 truncate text-right font-mono text-[11px] ${style.text}`}
                      title={detail}
                    >
                      {detail ?? "—"}
                    </span>
                  ) : s.health === "unknown" ? (
                    // 🔴 复用既有键：「未探测」是有信息量的（提示去探测），保留；
                    //    ok 行则**不再渲染任何文字**（2026-10-08 第 7 轮）——
                    //    header 的「全部正常」徽标是汇总的唯一出口，行内再印一遍
                    //    等于一屏 5 次重复（截图实测），且稀释了绿点本身的信号价值。
                    //    另起新键 status.normal 等于造第二份同义文案，不改。
                    <span className={`${F.label} min-w-0 flex-1 truncate text-right`}>
                      {t("status.notProbed")}
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1" aria-hidden />
                  )}

                  <span className={`${F.deco} hidden shrink-0 tabular-nums sm:inline`}>
                    {timeLabel(s.updatedAt)}
                  </span>
                  {/* 只有 warn/error 给定位入口：正常项给链接是纯噪声。
                      🔴 Link 是框架导航，basePath 由框架自动补（lib/base-path.ts 约定），
                      手工再拼一遍会产出双前缀 404 —— 与模块卡同一坑（2026-10-08）。 */}
                  {/* ① 看门狗行的「关闭」操作（常驻；已停/没在跑时置灰，状态由 detailKey 显示） */}
                  {s.action === "stopWatchdog" ? (
                    <HomeChip
                      onClick={() => void closeWatchdog()}
                      disabled={stoppingWatchdog || s.actionDisabled}
                      aria-label={t("status.closeWatchdog")}
                      title={t("status.closeWatchdog")}
                      data-service-action="stop-watchdog"
                      data-testid="stop-watchdog"
                      data-disabled={s.actionDisabled ? "1" : "0"}
                    >
                      {t("status.closeWatchdogShort")}
                    </HomeChip>
                  ) : s.locateHref && (s.health === "error" || s.health === "warn") ? (
                    <Link
                      href={`/${locale}${s.locateHref}`}
                      title={t("status.locate")}
                      aria-label={`${t("status.locate")} · ${tRoot(s.labelKey)}`}
                      className={`shrink-0 rounded p-0.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--surface)] hover:text-[var(--primary)]`}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="w-4 shrink-0" aria-hidden />
                  )}
                </li>
              );
            })}
          </ul>
          </div>
          <p className={`${F.deco} mt-1`}>
            {t("status.collectedAt")}: {timeLabel(data.collectedAt)}
          </p>
        </>
      ) : (
        <HomeEmpty title={t("status.noData")} compact />
      )}
    </HomeSection>
  );
}
